import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { scaleOrdinal } from 'd3-scale';
import { formatYAxis } from './charts.util';

export interface GroupedBarChartData {
  location: string;
  types: {
    projectType: string;
    avgValue: number;
  }[];
}

interface CCReportGroupedViewBarChartProps {
  data: GroupedBarChartData[];
}

// Define a color palette explicitly for project types
const colorPalette = ['#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F', '#EDC948', '#B07AA1', '#FF9DA7', '#9C755F', '#BAB0AC'];

// Assign colors based on the order of project types
const colorScale = (projectTypes: string[]) => {
  const scale = scaleOrdinal<string, string>().domain(projectTypes).range(colorPalette);
  return (type: string) => scale(type);
};

// Extract all unique project types from the dataset
const extractUniqueProjectTypes = (data: GroupedBarChartData[]) => {
  const projectTypesSet = new Set<string>();
  data.forEach(locationData => {
    locationData.types.forEach(type => {
      projectTypesSet.add(type.projectType);
    });
  });
  return Array.from(projectTypesSet).sort();
};

// Flatten the data for rendering in the chart
const flattenData = (data: GroupedBarChartData[], projectTypes: string[]) => {
  return data.map(locationData => {
    const flattenedLocation: { location: string; [key: string]: number | string } = { location: locationData.location };

    projectTypes.forEach(type => {
      const foundType = locationData.types.find(t => t.projectType === type);
      flattenedLocation[type] = foundType ? foundType.avgValue : 0; // Fill with 0 if no value for that type
    });

    return flattenedLocation;
  });
};

// Custom Legend for Project Types
const CustomLegend = ({ projectTypes, getColor }: { projectTypes: string[]; getColor: (type: string) => string }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginLeft: '20px' }}>
      <ul style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
        {projectTypes.map((type, index) => (
          <li
            key={`item-${index}`}
            style={{
              marginBottom: 8,
              fontSize: '14px',
              fontFamily: 'Arial, sans-serif',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {/* Bullet point with consistent size to align the text */}
            <span
              style={{
                display: 'inline-block',
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                backgroundColor: getColor(type),
                marginRight: '10px',
              }}
            />
            {type}
          </li>
        ))}
      </ul>
    </div>
  );
};

const CCReportGroupedViewBarChart: React.FC<CCReportGroupedViewBarChartProps> = ({ data }) => {
  const projectTypes = extractUniqueProjectTypes(data);
  const chartData = flattenData(data, projectTypes);

  // Get consistent color assignment for both chart and legend
  const getColor = colorScale(projectTypes);

  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
      {/* Bar Chart Container */}
      <div style={{ width: '75%' }}>
        <ResponsiveContainer width='100%' height={400}>
          <BarChart data={chartData} margin={{ top: 50, right: 30, left: 10, bottom: 50 }} barGap={5}>
            <CartesianGrid strokeDasharray='3 3' stroke='#d9d9d9' />
            <XAxis dataKey='location' tick={{ fontSize: 14, fontFamily: 'Arial, sans-serif' }} />
            <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 14, fontFamily: 'Arial, sans-serif' }} />
            <Tooltip formatter={(value: number) => `$${value.toFixed(2)}`} />
            {projectTypes.map((type, index) => (
              <Bar key={index} dataKey={type} name={type} fill={getColor(type)} barSize={40} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend on the right */}
      <CustomLegend projectTypes={projectTypes} getColor={getColor} />
    </div>
  );
};

export default CCReportGroupedViewBarChart;
